import ProductPrice from "./ProductPrice";
import ShopCategories from "./ShopCategories";
import { useCurrency } from "../currency/Currency";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import "../catalog.css";
import { useScrollIntoViewOnChange } from "../navigation/useScrollIntoViewOnChange";

const departments = [
  "All products",
  "Bill acceptors",
  "Parts & accessories",
  "Power & cables",
];
export default function AllProducts() {
  const { money, currency } = useCurrency();
  const location = useLocation();
  const [params,setParams]=useSearchParams();
  const search=(params.get('q')||'').slice(0,200);
  const [term,setTerm]=useState(search);
  const department=departments.includes(params.get('department')||'')?params.get('department')!:'All products';
  const sort=['menu_order','latest','low','high'].includes(params.get('sort')||'')?params.get('sort')!:'menu_order';
  const availability=['all','in-stock','sale'].includes(params.get('availability')||'')?params.get('availability')!:'all';
  const lowPrice=params.get('min')||'';
  const highPrice=params.get('max')||'';
  const [minDraft,setMinDraft]=useState(lowPrice);
  const [maxDraft,setMaxDraft]=useState(highPrice);
  const [filterError,setFilterError]=useState('');
  const [filtersOpen,setFiltersOpen]=useState(false);
  useEffect(()=>{setMinDraft(lowPrice);setMaxDraft(highPrice);},[lowPrice,highPrice]);
  const activeFilters=[sort!=='menu_order',availability!=='all',!!lowPrice,!!highPrice].filter(Boolean).length;
  const hasFilters=!!search||department!=='All products'||availability!=='all'||!!lowPrice||!!highPrice;
  const rawPage=Number(params.get('page')||1);
  const page=Number.isSafeInteger(rawPage)&&rawPage>0&&rawPage<=100000?rawPage:1;
  const pageSize=12;
  // Next/Previous land on the product count just above the products, not the
  // section heading (categories, search and filters sit in between).
  const productsTop=useRef<HTMLParagraphElement>(null);
  useEffect(()=>setTerm(search),[search]);
  // Every page change, including back to page 1, lands at the top of the products.
  useScrollIntoViewOnChange(productsTop,page);
  const updateCatalogue=(changes:Record<string,string>)=>{
    const next=new URLSearchParams(params);
    Object.entries(changes).forEach(([key,value])=>{if(!value||value==='menu_order'||value==='All products'||(key==='page'&&value==='1'))next.delete(key);else next.set(key,value);});
    // Filters and pages update the list in place: don't jump to the top.
    setParams(next,{preventScrollReset:true});
  };
  const catalogueReturn=location.pathname+location.search;
  const { data, isLoading, isError, refetch, isPreviousData } = useQuery(
    ["store-catalog", search, department, sort, page, availability, lowPrice, highPrice],
    async () => {
      const base = process.env.REACT_APP_SERVER_URL?.replace(/\/$/, "");
      if (!base) throw new Error("Catalogue unavailable");
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sort,
      });
      params.set('availability',availability);
      if(lowPrice)params.set('lowPrice',lowPrice);
      if(highPrice)params.set('highPrice',highPrice);
      if (search) params.set("search", search);
      if (department !== "All products") params.set("filter", department);
      const response = await axios.get(
        `${base}/product/browse?${params}`
      );
      const payload = response.data.data;
      return payload.data || payload;
    },
    // Keep the current products on screen (faded) until the next page arrives.
    { retry: false, keepPreviousData: true }
  );
  const changePage = (nextPage: number) => {
    updateCatalogue({page:String(nextPage)});
  };
  const reset = () => {setTerm('');setFilterError('');updateCatalogue({q:'',department:'',page:'1',min:'',max:'',availability:'',sort:''});};
  const totalPages = Math.max(1, Number(data?.totalPages) || 1);
  const pageNumbers = Array.from(new Set([1, totalPages, ...Array.from({length:5},(_,i)=>Math.min(Math.max(page-2,1),Math.max(1,totalPages-4))+i).filter(n=>n<=totalPages)])).sort((a,b)=>a-b);
  const products = data?.results || [];
  const count = data?.totalRecords || 0;
  return (
    <main className="catalog-page">
      <header className="catalog-intro">
        <div className="catalog-wrap">
          <p className="catalog-eyebrow">TECHNOGENIOUS / THE STORE</p>
          <h1>
            Keep your gaming machine
            <br />
            <span>working at its best.</span>
          </h1>
          <p>
            Explore components and accessories for your setup. Need help finding
            the right part? Our team can help.
          </p>
          <Link className="catalog-help" to="/contact">
            Talk to our team <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </header>
      <div className="catalog-benefits">
        <div className="catalog-wrap">
          <span>
            01 <strong>Free store pickup</strong>
          </span>
          <span>
            02 <strong>Local & international delivery</strong>
          </span>
          <span>
            03 <strong>Technical support</strong>
          </span>
        </div>
      </div>
      <section
        className={`catalog-wrap catalog-shop${filtersOpen ? " catalog-filters-open" : ""}`}
        aria-label="Product catalogue"
      >
        <div className="catalog-heading">
          <div>
            <p className="catalog-eyebrow">FIND YOUR NEXT PART</p>
            <h2>Shop the collection</h2>
          </div>
          <p>
            Prices in {currency}
            {currency === "JMD" ? " · approximate" : ""}
          </p>
        </div>
        <ShopCategories />
        <form
          className="catalog-search"
          onSubmit={(e) => {
            e.preventDefault();
            updateCatalogue({q:term.trim(),page:'1'});
          }}
        >
          <label className="sr-only" htmlFor="catalog-search">
            Search the catalogue
          </label>
          <input
            id="catalog-search"
            value={term}
            maxLength={200}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search products by name…"
            type="search"
          />
          <button type="submit">Search</button>
        </form>
        <div className="catalog-toolbar">
          <div
            className="catalog-tabs"
            role="group"
            aria-label="Product departments"
          >
            {departments.map((name) => (
              <button
                key={name}
                aria-pressed={department === name}
                onClick={() => {
                  updateCatalogue({department:name,page:'1'});
                }}
              >
                {name}
              </button>
            ))}
          </div>
          <label className="catalog-sort">
            Sort by{" "}
            <select
              value={sort}
              onChange={(e) => {
                updateCatalogue({sort:e.target.value,page:'1'});
              }}
            >
              <option value="menu_order">Featured</option>
              <option value="latest">Newest</option>
              <option value="low">Price: low to high</option>
              <option value="high">Price: high to low</option>
            </select>
          </label>
        </div>
        {/* Shown on small screens only: sort and filters start collapsed there. */}
        <button type="button" className="catalog-filter-toggle" aria-expanded={filtersOpen} aria-controls="catalog-filter-panel" onClick={()=>setFiltersOpen(!filtersOpen)}>
          {filtersOpen ? "Hide filters" : `Sort & filter${activeFilters ? ` (${activeFilters})` : ""}`}
        </button>
        <form id="catalog-filter-panel" className="catalog-filters" aria-label="Filter products" onSubmit={e=>{
          e.preventDefault();
          if((minDraft!==''&&(!Number.isFinite(Number(minDraft))||Number(minDraft)<0))||(maxDraft!==''&&(!Number.isFinite(Number(maxDraft))||Number(maxDraft)<0))||(minDraft!==''&&maxDraft!==''&&Number(minDraft)>Number(maxDraft))){setFilterError('Enter prices of 0 or more, with the minimum no higher than the maximum.');return;}
          setFilterError('');updateCatalogue({min:minDraft,max:maxDraft,page:'1'});
        }}>
          <label>Availability<select value={availability} onChange={e=>updateCatalogue({availability:e.target.value,page:'1'})}>
            <option value="all">All products</option><option value="in-stock">In stock</option><option value="sale">On sale</option>
          </select></label>
          <label>Min price (USD)<input type="number" min="0" step="0.01" value={minDraft} onChange={e=>setMinDraft(e.target.value)} placeholder="No minimum" /></label>
          <label>Max price (USD)<input type="number" min="0" step="0.01" value={maxDraft} onChange={e=>setMaxDraft(e.target.value)} placeholder="No maximum" /></label>
          <button type="submit">Apply prices</button>
          {hasFilters&&<button type="button" onClick={reset}>Reset filters</button>}
          <p>Price filters use USD. For products with options, the lowest confirmed option price is used.</p>
          {filterError&&<p role="alert">{filterError}</p>}
        </form>
        {products.some((p: any) => p.tags?.includes("staging-demo")) && (
          <p className="catalog-notice">
            STAGING PREVIEW · Sample products, illustrations and prices for
            testing only. These are not sales offers.
          </p>
        )}
        <p ref={productsTop} className="catalog-results" role="status" style={{ scrollMarginTop: 16 }}>
          {isLoading
            ? "Loading products…"
            : isError
            ? "Catalogue temporarily unavailable"
            : `${count} ${count === 1 ? "product" : "products"}${
                search ? ` matching “${search}”` : ""
              }`}
        </p>
        {isError ? (
          <div className="catalog-empty">
            <h3>We couldn’t load the catalogue.</h3>
            <p>Please try again in a moment.</p>
            <button onClick={() => refetch()}>Try again</button>
          </div>
        ) : isLoading ? (
          <div className="catalog-grid" aria-hidden="true">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div className="catalog-skeleton" key={i} />
            ))}
          </div>
        ) : !products.length ? (
          <div className="catalog-empty">
            <h3>
              {hasFilters
                ? "No products match your search."
                : "Our catalogue is being prepared."}
            </h3>
            <p>
              {hasFilters
                ? "Try another name or browse all products."
                : "Contact our team for help finding a part."}
            </p>
            {hasFilters ? (
              <button onClick={reset}>Clear filters</button>
            ) : (
              <Link to="/contact">Contact us</Link>
            )}
          </div>
        ) : (
          <div className="catalog-grid" aria-busy={isPreviousData} style={isPreviousData ? { opacity: 0.55, transition: "opacity .2s" } : undefined}>
            {products.map((p: any) => (
              <article className="catalog-card" key={p._id}>
                <Link
                  className="catalog-image"
                  to={`/product/${p._id}`} state={{catalogueReturn}}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <img
                    src={p.image || "/catalog/placeholder.svg"}
                    alt={p.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/catalog/placeholder.svg";
                    }}
                  />
                  {p.tags?.includes("staging-demo") && <span>Sample</span>}
                </Link>
                <div className="catalog-card-body">
                  {p.featured && <span className="promotion-badge">Featured</span>}
                  <p className="catalog-category">
                    {p.tags?.find((t: string) => departments.includes(t)) ||
                      "Gaming machines & accessories"}
                  </p>
                  <h3>
                    <Link to={`/product/${p._id}`} state={{catalogueReturn}}>{p.name}</Link>
                  </h3>
                  <p
                    className={
                      p.catalogueInStock
                        ? "catalog-stock"
                        : "catalog-stock unavailable"
                    }
                  >
                    {p.catalogueInStock ? (p.catalogueHasOptions ? "● Options in stock" : "● In stock") : p.cataloguePrice == null ? "Confirm availability" : "Out of stock"}
                  </p>
                  <div className="catalog-card-bottom">
                    <strong>
                      {p.cataloguePrice == null ? "Request a quote" : p.catalogueHasOptions ? <>From {money(p.cataloguePrice)}</> : <ProductPrice price={p.cataloguePrice} compareAtPrice={p.compareAtPrice}/>}
                    </strong>
                    <Link
                      to={`/product/${p._id}`} state={{catalogueReturn}}
                      aria-label={`View ${p.name}`}
                    >
                      View item <span aria-hidden="true">↗</span>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        {!isLoading && !isError && data?.totalPages > 1 && (
          <nav className="catalog-pagination" aria-label="Catalogue pages">
            <button disabled={page === 1} onClick={() => changePage(page - 1)}>
              Previous
            </button>
            <div className="catalog-page-numbers">
              {pageNumbers.map((number,index)=><React.Fragment key={number}>
                {index>0 && number-pageNumbers[index-1]>1 && <span className="catalog-page-gap" aria-hidden="true">…</span>}
                <button type="button" aria-label={`Page ${number}`} aria-current={page===number?"page":undefined} onClick={()=>{if(number!==page)changePage(number);}}>{number}</button>
              </React.Fragment>)}
            </div>
            <button
              disabled={page >= data.totalPages}
              onClick={() => changePage(page + 1)}
            >
              Next
            </button>
            <span className="catalog-page-status" aria-live="polite">Page {page} of {totalPages}</span>
          </nav>
        )}
        <aside className="catalog-assistance">
          <div>
            <h2>Not sure which part fits?</h2>
            <p>
              Tell us about your gaming machine. We’ll help you find the right
              match.
            </p>
          </div>
          <Link to="/contact">Get product advice ↗</Link>
        </aside>
      </section>
    </main>
  );
}
